import { queryOptions } from "@tanstack/react-query";
import { authService } from "./auth.service";
import type { ActionToCheck } from "./auth.types";

// ==================== QUERY KEYS ====================

export const authKeys = {
	all: ["auth"] as const,
	userInfo: () => [...authKeys.all, "userInfo"] as const,
	permissions: () => [...authKeys.all, "permissions"] as const,
	userPermissions: (userId: string) =>
		[...authKeys.all, "userPermissions", userId] as const,
	checkActionRoot: () => [...authKeys.all, "checkAction"] as const,
	checkActionsRoot: () => [...authKeys.all, "checkActions"] as const,
	checkAction: (action: ActionToCheck) =>
		[
			...authKeys.checkActionRoot(),
			action.name,
			action.entityType,
			action.entityId,
			action.subOrganizationId,
		] as const,
	checkActions: (actions: ActionToCheck[]) =>
		[
			...authKeys.checkActionsRoot(),
			// Sort action names for consistent cache key regardless of order
			...actions
				.map(
					(a) =>
						`${a.name}:${a.entityType}:${a.entityId}:${a.subOrganizationId}`,
				)
				.sort(),
		] as const,
};

// ==================== QUERY OPTIONS ====================

export const authQueryOptions = {
	userInfo: () =>
		queryOptions({
			queryKey: authKeys.userInfo(),
			queryFn: async () => {
				return await authService.getUserInfo();
			},
			// Don't retry on ANY failure — UserInfoError wraps axios errors and doesn't
			// satisfy isApiError(), so the original 4xx guard never fires.  Auth checks are
			// called again by the route guards / handleOAuthReturn, so retrying here only
			// creates unnecessary noise and can interfere with the OAuth session flow.
			retry: false,
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
			throwOnError: false,
		}),

	/**
	 * Current user's permissions query
	 */
	permissions: () =>
		queryOptions({
			queryKey: authKeys.permissions(),
			queryFn: async () => {
				return await authService.getCurrentUserPermissions();
			},
			staleTime: 5 * 60 * 1000, // 5 minutes - permissions don't change often
			gcTime: 10 * 60 * 1000, // 10 minutes
			retry: false, // Don't retry on failure — each retry triggers the 401 interceptor
		}),

	/**
	 * Specific user's permissions query
	 */
	userPermissions: (userId: string) =>
		queryOptions({
			queryKey: authKeys.userPermissions(userId),
			queryFn: async () => {
				return await authService.getUserPermissions(userId);
			},
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	/**
	 * Check a single action permission
	 * Key includes action name, entityType, and entityId to prevent refetching
	 */
	checkAction: (action: ActionToCheck) =>
		queryOptions({
			queryKey: authKeys.checkAction(action),
			queryFn: async () => {
				return await authService.checkAction(action);
			},
			staleTime: 1 * 60 * 1000, // 1 minute - permissions don't change often
			gcTime: 1 * 60 * 1000, // 1 minute
			retry: false,
			throwOnError: false,
		}),

	/**
	 * Check multiple actions at once
	 * Key includes all action names (sorted) to prevent refetching
	 */
	checkActions: (actions: ActionToCheck[]) =>
		queryOptions({
			queryKey: authKeys.checkActions(actions),
			queryFn: async () => {
				return await authService.checkActions(actions);
			},
			staleTime: 1 * 60 * 1000, // 1 minute - permissions don't change often
			gcTime: 1 * 60 * 1000, // 1 minute
			retry: false,
			throwOnError: false,
		}),
};
